import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CelebrationIcon from '@mui/icons-material/Celebration';
import swimService from '../../api/services/swimService';
import { RosterAttendee, SwimEventRow } from '../../types/swim';

interface Props {
  event: SwimEventRow;
  onClose: () => void;
}

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}
function fmtTime(value: string | null): string {
  if (!value) return '';
  const d = value.includes('T') ? new Date(value) : new Date(`1970-01-01T${value}`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** Club event detail: info + who's RSVP'd (team-wide, staff view). */
const EventDetailDialog: React.FC<Props> = ({ event, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const timeRange = [fmtTime(event.start_time), fmtTime(event.end_time)].filter(Boolean).join(' – ');
  const [swimmers, setSwimmers] = useState<RosterAttendee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const roster = await swimService.getEventRoster(event.id);
        setSwimmers(roster.swimmers ?? []);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load event');
      } finally {
        setLoading(false);
      }
    })();
  }, [event.id]);

  return (
    <Dialog open fullScreen={fullScreen} fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <CelebrationIcon sx={{ color: theme.palette.info.main }} />
          <span>{event.name}</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={0.25} sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {fmtDate(event.event_date)}
            {timeRange ? ` · ${timeRange}` : ''}
          </Typography>
          {event.location && <Typography variant="body2" color="text.secondary">{event.location}</Typography>}
          {event.notes && <Typography variant="body2" sx={{ mt: 0.5 }}>{event.notes}</Typography>}
        </Stack>

        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Attending ({swimmers.length})</Typography>
            {swimmers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No one has RSVP'd yet.</Typography>
            ) : (
              <Stack spacing={1}>
                {swimmers.map((s) => (
                  <Box key={s.member_id} sx={{ p: 1.5, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {s.first_name} {s.last_name}
                      {s.age != null ? ` · ${s.age}` : ''}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventDetailDialog;
